<template>
  <div class="promo-card">
    <div class="promo-card__header">
      <h3 class="promo-card__title">{{ promotion.title }}</h3>
      <span class="promo-card__retailer">{{ promotion.retailerName }}</span>
    </div>

    <p class="promo-card__description">{{ promotion.description }}</p>

    <!-- Indicador de caducitat -->
    <div v-if="expiresWithinDays(7)" class="promo-card__expiry">
      ⏰ {{ expiryText }}
    </div>

    <!-- Codi de cupó -->
    <div v-if="promotion.couponType === 'code'" class="promo-card__code-section">
      <div 
        class="promo-card__code"
        @click="copyCode"
        :class="{ 'promo-card__code--copied': copied }"
      >
        {{ copied ? '✓ ' + $t("mylist.deals.modal.copiedCode") : promotion.couponCode }}
      </div>
    </div>

    <!-- Botó per anar a la promo -->
    <a 
      v-if="promotion.linkUrl"
      :href="promotion.linkUrl" 
      target="_blank"
      class="promo-card__link"
    >
      {{ $t("mylist.deals.modal.goToPromotion") }} →
    </a>
  </div>
</template>

<script>
export default {
  name: 'PromotionCard',
  props: {
    promotion: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      copied: false,
    };
  },
  computed: {
    expiryText() {
      if (!this.promotion.endAt) return '';
      
      const now = new Date();
      const endDate = new Date(this.promotion.endAt);
      const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft === 0) return this.$t("mylist.deals.expiresEndsToday") || 'Termina hoy';
      if (daysLeft === 1) return this.$t("mylist.deals.expiresEndsTomorrow") || 'Termina mañana';
      return this.$t("mylist.deals.expiresInDays", { days: daysLeft }) || `Expira en ${daysLeft} días`;
    },
  },
  methods: {
    expiresWithinDays(days) {
      if (!this.promotion.endAt) return false;
      
      const now = new Date();
      const endDate = new Date(this.promotion.endAt);
      const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      
      return daysLeft >= 0 && daysLeft <= days;
    },
    
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.promotion.couponCode);
        this.copied = true;
        
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Error copiant codi:', err);
        this.fallbackCopyCode();
      }
    },
    
    fallbackCopyCode() {
      const textArea = document.createElement('textarea');
      textArea.value = this.promotion.couponCode;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      
      document.body.removeChild(textArea);
    },
  },
};
</script>

<style scoped>
.promo-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  transition: all 0.2s;
}

.promo-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.promo-card__header {
  margin-bottom: 12px;
}

.promo-card__title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px 0;
}

.promo-card__retailer {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.promo-card__description {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.promo-card__expiry {
  background: #fef3c7;
  color: #92400e;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 12px;
}

.promo-card__code-section {
  margin-bottom: 12px;
}

.promo-card__code {
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 16px;
  color: #111827;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.promo-card__code:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.promo-card__code:active {
  transform: scale(0.98);
}

.promo-card__code--copied {
  background: #d1fae5;
  border-color: #10b981;
  color: #065f46;
}

.promo-card__link {
  display: inline-block;
  color: #6b7280;
  font-size: 14px;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.promo-card__link:hover {
  color: #111827;
  text-decoration: underline;
}
</style>
